// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Card, Group, Loader, Stack, Text, Title, Tooltip } from '@mantine/core';

import { ApiError } from '../../api/client';
import { observabilityApi } from '../../api/observability';
import type { GatewayState, GatewayStatus } from '../../api/observability';

/**
 * Whether a Gateway is connected, and when it was last heard from.
 *
 * # THE DISTINCTION THIS COMPONENT EXISTS TO MAKE VISIBLE
 *
 *     ONLINE      the Server has an authenticated session RIGHT NOW
 *     LAST SEEN   when it last heard anything, which may be long ago
 *
 * They are rendered as two separate things because they are two separate facts,
 * and the failure this guards against is a display that quietly merges them:
 *
 *     Offline
 *     Last seen 2 seconds ago
 *
 * is a real and common state — a Server that has just restarted has an empty
 * session registry and a very recent timestamp — and a card that showed "Online"
 * there would be lying at exactly the moment an operator is trying to find out
 * what is wrong.
 *
 * # THE BROWSER IS NOT PART OF LIVENESS
 *
 * Nothing here computes connectivity. `state` arrives decided by the Server,
 * which asks its session registry. This file has no threshold constant and no
 * comparison against `last_seen_at`, deliberately: a component that could
 * decide "recent enough is online" would be a second definition of connectivity
 * living in the least reviewable place.
 */
export function GatewayStatusCard({ gatewayId }: { gatewayId: string }) {
  const [status, setStatus] = useState<GatewayStatus | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setStatus(await observabilityApi.gatewayStatus(gatewayId));
      setError(null);
    } catch (err) {
      // THE PREVIOUS ANSWER IS DISCARDED.
      //
      // Keeping it would leave "ONLINE" on screen while the Server is
      // unreachable — stale data inventing connectivity, which is the same lie
      // this component exists to prevent, arrived at from the other direction.
      setStatus(null);
      setError(err as ApiError);
    } finally {
      setLoading(false);
    }
  }, [gatewayId]);

  useEffect(() => {
    void load();

    // POLLING, USING THE ORDINARY REQUEST PATH.
    //
    // Not a WebSocket. The browser talks to the Server over the same
    // authenticated API it uses for everything else, and the Gateway talks to
    // the Server over its own channel. Joining those would make a browser tab
    // part of liveness determination, which it must never be.
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (loading && !status && !error) {
    return (
      <Card withBorder>
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Checking the Gateway…
          </Text>
        </Group>
      </Card>
    );
  }

  if (error) {
    return (
      <Card withBorder>
        <Stack gap="xs">
          <Title order={4}>Gateway</Title>
          {/* AN API FAILURE IS NOT A GATEWAY STATE. Showing "Offline" here would
              blame the Gateway for the Server being unreachable, and an operator
              would go and restart the wrong thing. */}
          <Alert color="yellow" title="Status unavailable">
            The Server could not be asked about this Gateway, so its current state is
            unknown. This says nothing about whether the Gateway is running.
          </Alert>
        </Stack>
      </Card>
    );
  }

  if (!status) return null;

  const look = APPEARANCE[status.state];

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={4}>Gateway</Title>
          <Tooltip label={look.explanation} multiline w={280}>
            <Badge color={look.colour} size="lg" variant="filled">
              {look.label}
            </Badge>
          </Tooltip>
        </Group>

        {/* LAST CONTACT IS ALWAYS ITS OWN LINE, whatever the state. That is what
            makes "Offline / last seen 2 seconds ago" expressible. */}
        <Field label="Last contact">{describeLastContact(status)}</Field>

        {status.connected_since && (
          <Field label="Connected since">{absolute(status.connected_since)}</Field>
        )}
        {status.last_heartbeat && (
          <Field label="Last heartbeat">{relative(status.last_heartbeat)}</Field>
        )}
        {status.agent_version && (
          <Field label="Version">
            {/* REPORTED, NOT VERIFIED. Said plainly rather than presented as a
                fact Meshvara established. */}
            <Tooltip label="What the Gateway reports it is running. Meshvara does not verify this.">
              <span>{status.agent_version}</span>
            </Tooltip>
          </Field>
        )}
        {status.state !== 'ONLINE' && status.last_disconnect_reason && (
          <Field label="Last disconnect">{DISCONNECT_REASONS[status.last_disconnect_reason] ?? status.last_disconnect_reason}</Field>
        )}

        <Text size="xs" c="dimmed">
          Checked {relative(status.observed_at)}.
        </Text>
      </Stack>
    </Card>
  );
}

/** How often the card re-asks the Server. */
const REFRESH_MS = 10_000;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed" w={130}>
        {label}
      </Text>
      <Text size="sm">{children}</Text>
    </Group>
  );
}

/**
 * What each state looks like, and what it means.
 *
 * The explanations are the useful part: an operator who reads "Degraded" needs
 * to know whether to act, and "the session is open but it has stopped saying so"
 * is actionable where a colour is not.
 */
const APPEARANCE: Record<GatewayState, { label: string; colour: string; explanation: string }> = {
  ONLINE: {
    label: 'Online',
    colour: 'green',
    explanation: 'The Server has an authenticated connection to this Gateway right now.',
  },
  DEGRADED: {
    label: 'Degraded',
    colour: 'yellow',
    explanation:
      'The connection is open but the Gateway has stopped sending heartbeats on time. ' +
      'Work may still succeed. Worth looking at.',
  },
  OFFLINE: {
    label: 'Offline',
    colour: 'red',
    explanation:
      'The Server has no connection to this Gateway. It has connected before — see the ' +
      'last contact time below.',
  },
  REVOKED: {
    label: 'Revoked',
    colour: 'gray',
    explanation:
      'This Gateway has been administratively revoked and cannot be given work, ' +
      'whether or not it is running.',
  },
  NEVER_CONNECTED: {
    label: 'Never connected',
    colour: 'gray',
    explanation:
      'This Gateway has been enrolled but has never reached the Server. It has most ' +
      'likely not been started yet.',
  },
};

/** The closed set of disconnect reasons, in operator words. */
const DISCONNECT_REASONS: Record<string, string> = {
  client_closed: 'The Gateway closed the connection.',
  server_shutdown: 'The Server shut down.',
  heartbeat_timeout: 'The Gateway stopped responding.',
  revoked: 'The Gateway was revoked while connected.',
  protocol_error: 'The Gateway sent something unexpected.',
  transport_error: 'The connection failed.',
  superseded: 'A newer connection from the same Gateway replaced it.',
};

/**
 * The last-contact line.
 *
 * NEVER CONNECTED is its own sentence rather than an empty field, because a
 * blank would read as missing data rather than as a fact.
 */
function describeLastContact(status: GatewayStatus): string {
  if (status.state === 'ONLINE' || status.state === 'DEGRADED') {
    return status.last_heartbeat ? relative(status.last_heartbeat) : 'just now';
  }
  if (!status.last_seen_at) {
    return 'never — this Gateway has not reached the Server';
  }
  return relative(status.last_seen_at);
}

/**
 * A human interval.
 *
 * # WHY THE PRECISION DEGRADES WITH AGE
 *
 * "4 minutes ago" is useful; "4 minutes and 12 seconds ago" is false precision
 * on a value the Server persists at most once a minute. Claiming resolution the
 * data does not have would invite an operator to read meaning into a number that
 * has none.
 */
function relative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'at an unknown time';

  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return absolute(iso);
}

/** An absolute timestamp, for anything old enough that an interval stops helping. */
function absolute(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'an unknown time';
  return at.toLocaleString();
}
